import React, { useMemo, useState } from 'react';
import { db, appId } from '../utils/firebase';
import { updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { Icons } from '../components/Icons';
import AddDataModal from '../components/AddDataModal';
import { CARD_KINDS, STATUS_LABEL } from '../utils/crew';

// For the team: cards that crew members (artists, musicians, writers, helpers) have drafted.
// Open one to finish it: set the code, place it on the map (Geospatial Tracking), then mark it VERIFIED.
const SubmissionsView = ({ data, isAdmin, showToast }) => {
    const [filter, setFilter] = useState('DRAFT');
    const [editing, setEditing] = useState(null);
    const [search, setSearch] = useState('');

    const all = useMemo(() => [
        ...(data.artifacts || []).filter(a => a.createdBy).map(a => ({ ...a, _col: 'artifacts' })),
        ...(data.ideas || []).filter(a => a.createdBy).map(a => ({ ...a, _col: 'ideas' })),
    ].sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)), [data.artifacts, data.ideas]);

    const counts = useMemo(() => ({
        DRAFT: all.filter(i => (i.status || 'DRAFT') === 'DRAFT').length,
        VERIFIED: all.filter(i => i.status === 'VERIFIED').length,
        ALL: all.length,
    }), [all]);

    const shown = all.filter(i => (filter === 'ALL' || (i.status || 'DRAFT') === filter)
        && (!search.trim() || `${i.title || ''} ${i.createdByHandle || ''} ${i.suggestedSite || ''}`.toLowerCase().includes(search.trim().toLowerCase())));

    const save = async (formData) => {
        try {
            const col = editing._col;
            const { _col, ...clean } = formData; // _col is only for this list, never saved
            await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', col, formData.id), { ...clean, status: clean.status || 'DRAFT' });
            showToast('Saved.', 'success');
            setEditing(null);
        } catch (e) { showToast('Could not save that card.', 'error'); }
    };
    const remove = async (item) => {
        if (!window.confirm(`Delete "${item.title}" from ${item.createdByHandle || 'a crew member'}? This cannot be undone.`)) return;
        try { await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', item._col, item.id)); showToast('Deleted.', 'success'); }
        catch (e) { showToast('Could not delete that card.', 'error'); }
    };

    const chip = (id, text, n) => (
        <button onClick={() => setFilter(id)} className={`px-4 py-2 text-[10px] font-bold uppercase tracking-widest rounded border ${filter === id ? 'bg-green-900/40 border-green-700 text-green-300' : 'border-gray-800 text-gray-500 hover:text-white'}`}>{text} ({n})</button>
    );

    return (
        <div className="absolute inset-0 overflow-y-auto p-6 bg-[#050505] custom-scrollbar fade-in pb-24" data-testid="submissions">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold flex items-center gap-2 text-white tracking-tight"><Icons.UploadCloud className="text-green-500" /> CREW SUBMISSIONS</h2>
            </div>
            <p className="text-xs text-gray-500 mb-5 max-w-2xl">Cards your artists, musicians, writers and helpers have drafted. Open one to finish it: add the code, place it on the map, then mark it Verified. Nothing reaches the game until you do.</p>
            <div className="flex flex-wrap gap-2 items-center mb-5">
                {chip('DRAFT', 'Needs review', counts.DRAFT)}
                {chip('VERIFIED', 'Approved', counts.VERIFIED)}
                {chip('ALL', 'All', counts.ALL)}
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search title, person or site..." className="ml-auto min-w-[200px] bg-[#0a0a0a] border border-gray-800 text-white p-2 text-xs outline-none focus:border-green-600 font-mono rounded" />
            </div>

            {shown.length === 0 ? (
                <div className="text-gray-600 text-sm font-mono mt-4 border border-dashed border-gray-800 p-10 text-center">
                    {all.length === 0 ? 'NO SUBMISSIONS YET. They appear here as soon as a crew member saves a card.' : 'Nothing matches.'}
                </div>
            ) : (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    {shown.map(item => (
                        <div key={item._col + item.id} className="bg-[#0a0a0a] border border-gray-900 rounded p-4 flex gap-4 shadow-lg">
                            {item.imageUrl ? <img src={item.imageUrl} alt="" className="w-24 h-24 object-cover rounded border border-gray-800 shrink-0" /> : <div className="w-24 h-24 rounded border border-gray-900 bg-black shrink-0 flex items-center justify-center text-gray-700"><Icons.Box size={24} /></div>}
                            <div className="flex-1 min-w-0 text-xs text-gray-300 space-y-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h4 className="font-bold text-white text-sm truncate">{item.title}</h4>
                                    <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border border-gray-700 text-gray-400">{(CARD_KINDS[item.kind] || {}).label || 'Card'}</span>
                                    <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${item.status === 'VERIFIED' ? 'border-green-800 text-green-400' : 'border-yellow-800 text-yellow-500'}`}>{STATUS_LABEL(item.status)}</span>
                                    {item.collab && <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border border-blue-900 text-blue-400">Collab</span>}
                                </div>
                                <div className="text-gray-500">from <span className="text-gray-300">{item.createdByHandle || 'unknown'}</span>{item.collab && item.collaborators ? <> with <span className="text-gray-300">{item.collaborators}</span></> : null}</div>
                                {item.suggestedSite && <div>Suggested site: <span className="text-gray-100">{item.suggestedSite}</span></div>}
                                {item.hint && <div className="truncate">Clue: <span className="text-gray-100">{item.hint}</span></div>}
                                {item.nextLead && <div className="truncate">Leads to: <span className="text-gray-100">{item.nextLead}</span></div>}
                                {isAdmin && (
                                    <div className="flex gap-3 pt-2">
                                        <button onClick={() => setEditing(item)} className="px-3 py-1.5 text-[10px] font-bold tracking-widest uppercase bg-green-900/40 border border-green-900 text-green-400 hover:bg-green-800 hover:text-white rounded">Open and finish</button>
                                        <button onClick={() => remove(item)} className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-900/20 rounded" title="Delete"><Icons.Trash2 size={14} /></button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {editing && (
                <AddDataModal type={editing._col === 'artifacts' ? 'artifact' : 'idea'} initialData={editing} onClose={() => setEditing(null)} onSubmit={save} showToast={showToast} isAdmin={isAdmin} />
            )}
        </div>
    );
};

export default SubmissionsView;
